<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Icon from '@/Components/Icon.vue';

const COLORS = ['#519acf', '#33ad73', '#ffd25e', '#fb8e50', '#f2585c'];
const MAX_DIMENSION = 2048;

const props = defineProps({
    image: { type: [Blob, String], required: true },
    drawing: { type: [Blob, String], default: null },
});

const emit = defineEmits(['close', 'save', 'error']);
const imageCanvas = ref(null);
const drawingCanvas = ref(null);
const loading = ref(true);
const saving = ref(false);
const selectedColor = ref(COLORS[0]);
const strokeWidth = ref(7);
const commands = ref([]);
const commandIndex = ref(0);
const activeStroke = ref(null);
let backgroundImage = null;
let initialDrawingImage = null;
const objectUrls = new Set();

const canUndo = computed(() => commandIndex.value > 0);
const canRedo = computed(() => commandIndex.value < commands.value.length);
const modified = computed(() => commandIndex.value > 0);
const canSave = computed(() => modified.value && !saving.value);

function sourceUrl(source) {
    if (source instanceof Blob) {
        const url = URL.createObjectURL(source);
        objectUrls.add(url);
        return url;
    }

    return source;
}

function loadImage(source) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Impossibile caricare l’immagine per il disegno.'));
        image.src = sourceUrl(source);
    });
}

function canvasDimensions(image) {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));

    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

function drawStroke(context, stroke) {
    const [firstPoint, ...remainingPoints] = stroke.points;
    if (!firstPoint) return;

    context.save();
    context.strokeStyle = stroke.color;
    context.fillStyle = stroke.color;
    context.lineWidth = stroke.width;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    if (!remainingPoints.length) {
        context.beginPath();
        context.arc(firstPoint.x, firstPoint.y, stroke.width / 2, 0, Math.PI * 2);
        context.fill();
    } else {
        context.beginPath();
        context.moveTo(firstPoint.x, firstPoint.y);
        remainingPoints.forEach(point => context.lineTo(point.x, point.y));
        context.stroke();
    }
    context.restore();
}

function redrawDrawing() {
    const canvas = drawingCanvas.value;
    const context = canvas?.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    if (initialDrawingImage) {
        context.drawImage(initialDrawingImage, 0, 0, canvas.width, canvas.height);
    }

    commands.value.slice(0, commandIndex.value).forEach(command => {
        if (command.type === 'clear') {
            context.clearRect(0, 0, canvas.width, canvas.height);
        } else {
            drawStroke(context, command);
        }
    });

    if (activeStroke.value) drawStroke(context, activeStroke.value);
}

function appendCommand(command) {
    commands.value = [...commands.value.slice(0, commandIndex.value), command];
    commandIndex.value = commands.value.length;
    redrawDrawing();
}

function pointFromEvent(event) {
    const canvas = drawingCanvas.value;
    const bounds = canvas.getBoundingClientRect();

    return {
        x: (event.clientX - bounds.left) * (canvas.width / bounds.width),
        y: (event.clientY - bounds.top) * (canvas.height / bounds.height),
    };
}

function startDrawing(event) {
    if (loading.value || saving.value || event.button > 0) return;
    drawingCanvas.value.setPointerCapture?.(event.pointerId);
    activeStroke.value = {
        type: 'stroke',
        color: selectedColor.value,
        width: Number(strokeWidth.value),
        points: [pointFromEvent(event)],
    };
    redrawDrawing();
}

function continueDrawing(event) {
    if (!activeStroke.value) return;
    activeStroke.value.points.push(pointFromEvent(event));
    redrawDrawing();
}

function finishDrawing(event) {
    if (!activeStroke.value) return;
    drawingCanvas.value.releasePointerCapture?.(event.pointerId);
    const stroke = activeStroke.value;
    activeStroke.value = null;
    appendCommand(stroke);
}

function undo() {
    if (!canUndo.value) return;
    commandIndex.value -= 1;
    redrawDrawing();
}

function redo() {
    if (!canRedo.value) return;
    commandIndex.value += 1;
    redrawDrawing();
}

function clearDrawing() {
    appendCommand({ type: 'clear' });
}

function hasVisibleDrawing() {
    let visible = initialDrawingImage !== null;
    commands.value.slice(0, commandIndex.value).forEach(command => {
        visible = command.type === 'clear' ? false : true;
    });
    return visible;
}

async function saveDrawing() {
    if (!canSave.value) return;
    if (!hasVisibleDrawing()) {
        emit('save', null);
        return;
    }

    saving.value = true;
    try {
        const blob = await new Promise(resolve => drawingCanvas.value.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Impossibile generare il disegno PNG.');
        emit('save', blob);
    } catch (error) {
        emit('error', error);
    } finally {
        saving.value = false;
    }
}

async function initialize() {
    loading.value = true;
    try {
        backgroundImage = await loadImage(props.image);
        initialDrawingImage = props.drawing ? await loadImage(props.drawing) : null;
        const dimensions = canvasDimensions(backgroundImage);

        await nextTick();
        for (const canvas of [imageCanvas.value, drawingCanvas.value]) {
            canvas.width = dimensions.width;
            canvas.height = dimensions.height;
        }
        imageCanvas.value.getContext('2d').drawImage(
            backgroundImage,
            0,
            0,
            dimensions.width,
            dimensions.height,
        );
        redrawDrawing();
    } catch (error) {
        emit('error', error);
    } finally {
        loading.value = false;
    }
}

function handleKeydown(event) {
    if (event.key === 'Escape') emit('close');
    if (!(event.ctrlKey || event.metaKey)) return;
    if (event.key.toLowerCase() === 'z') {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
    }
}

onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
    initialize();
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown);
    objectUrls.forEach(url => URL.revokeObjectURL(url));
    objectUrls.clear();
});
</script>

<template>
    <div class="fixed inset-0 z-[10020] flex flex-col bg-black/80" role="dialog" aria-modal="true"
        aria-labelledby="drawing-editor-title">
        <header class="flex min-h-14 items-center justify-between gap-4 bg-white px-3 py-2 sm:px-5">
            <h2 id="drawing-editor-title" class="text-base font-semibold text-[#312D32]">Disegna sulla fotografia</h2>
            <button type="button" title="Chiudi senza salvare" aria-label="Chiudi senza salvare"
                class="flex h-10 w-10 items-center justify-center text-[#312D32] disabled:opacity-40"
                :disabled="saving" @click="emit('close')">
                <Icon name="cross" class="h-5 w-5" />
            </button>
        </header>

        <div class="relative flex min-h-0 flex-1 items-center justify-center overflow-auto p-2 sm:p-5">
            <p v-if="loading" class="font-semibold text-white">Caricamento fotografia…</p>
            <div v-show="!loading" class="relative max-h-full max-w-full shadow-xl"
                :style="{ aspectRatio: `${imageCanvas?.width || 1} / ${imageCanvas?.height || 1}` }">
                <canvas ref="imageCanvas" class="block max-h-full max-w-full" />
                <canvas ref="drawingCanvas"
                    class="absolute inset-0 h-full w-full cursor-crosshair touch-none"
                    @pointerdown.prevent="startDrawing" @pointermove.prevent="continueDrawing"
                    @pointerup.prevent="finishDrawing" @pointercancel.prevent="finishDrawing" />
            </div>
        </div>

        <footer class="bg-white px-3 py-3 sm:px-5">
            <div class="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
                <div class="flex items-center gap-1">
                    <button type="button" title="Annulla" aria-label="Annulla"
                        class="flex h-11 w-11 items-center justify-center disabled:opacity-30"
                        :disabled="!canUndo || saving" @click="undo">
                        <Icon name="arrowShortLeft" class="h-5 w-5" />
                    </button>
                    <button type="button" title="Ripristina" aria-label="Ripristina"
                        class="flex h-11 w-11 items-center justify-center disabled:opacity-30"
                        :disabled="!canRedo || saving" @click="redo">
                        <Icon name="arrowShortRight" class="h-5 w-5" />
                    </button>
                    <button type="button" title="Cancella disegno" aria-label="Cancella disegno"
                        class="flex h-11 w-11 items-center justify-center text-[#A83B32] disabled:opacity-30"
                        :disabled="saving" @click="clearDrawing">
                        <Icon name="trash" class="h-5 w-5" />
                    </button>
                </div>

                <fieldset class="flex items-center gap-2" aria-label="Colore del tratto">
                    <label v-for="color in COLORS" :key="color" class="relative cursor-pointer">
                        <input v-model="selectedColor" class="peer sr-only" type="radio" name="drawing-color"
                            :value="color" :aria-label="`Colore ${color}`" />
                        <span class="block h-8 w-8 rounded-full border-2 border-white outline outline-1 outline-[#312D32]/20 peer-checked:outline-2 peer-checked:outline-[#312D32]"
                            :style="{ backgroundColor: color }" />
                    </label>
                </fieldset>

                <label class="flex min-w-44 flex-1 items-center gap-3 text-xs font-semibold text-[#4D4843] sm:max-w-60">
                    <span class="h-2 w-2 shrink-0 rounded-full bg-[#312D32]" aria-hidden="true" />
                    <input v-model.number="strokeWidth" class="min-w-0 flex-1 accent-[#312D32]" type="range"
                        min="2" max="12" step="1" aria-label="Spessore del tratto" />
                    <span class="h-5 w-5 shrink-0 rounded-full bg-[#312D32]" aria-hidden="true" />
                </label>

                <button type="button" aria-label="Salva disegno"
                    class="min-h-11 rounded-md px-5 text-sm font-semibold uppercase text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-principal"
                    :class="canSave
                        ? 'bg-principal shadow hover:bg-[var(--color-fondo-menu)]'
                        : 'cursor-not-allowed bg-[var(--color-contador)] opacity-60'"
                    :disabled="!canSave" @click="saveDrawing">
                    {{ saving ? 'Salvataggio…' : 'Salva' }}
                </button>
            </div>
        </footer>
    </div>
</template>